import React, { useEffect, useRef } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { UnsavedChangesButton, useUnsavedChanges } from './unsaved-changes'
import { ErrorReference } from '../error/error-reference'
import './sso-workspace.scss'

export const countDirty = dirty => {
  if (dirty === true) return 1
  if (!dirty || typeof dirty !== 'object') return 0
  return Object.values(dirty).reduce((n, v) => n + countDirty(v), 0)
}

export const unsavedText = count => (count === 1 ? '1 unsaved change' : `${count} unsaved changes`)

// The admin's one save bar (owner 2026-10-23: Save and Cancel must not float
// over a form as a card): docked full width to the bottom of the viewport,
// sliding in only while there is something unsaved (`on`), with what is
// unsaved on the left and Discard and Save on the right. The pane it sits in
// pads its foot while it shows (sso-workspace.scss), so nothing is hidden
// behind it. While hidden (moved off-screen) the bar is inert, so keyboard
// focus can't land on its buttons.
//
// `status` is what is unsaved: the connection workspace's list of changes
// (SaveBar below), else the count in words. `error`, a refused save, keeps
// the bar up; `failure`, the error behind it, adds its reference
// (error-reference.js). `submit`: Save is the form's submit button (FormNew
// runs its own handleSubmit); otherwise it calls onSave.
//
// When the bar goes away (Discard, a save that leaves nothing unsaved) with
// focus in it, focus moves to the page's heading -- `focusAfter()` names
// another place -- rather than falling to the page's <body> as the bar turns
// inert. The refused save's words are the bar's one alert: a page that also
// shows them elsewhere shows them there without role="alert".
const pageHeading = bar => {
  const page = bar.closest('.sw-pane, .lms-pane, .sw-workspace') || document
  return page.querySelector('h1') || page.querySelector('h2')
}
const FOCUSABLE = /^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/

export const DockedSaveBar = ({
  on, status, count = 0, error, failure, busy = false, canSave = true, submit = false,
  onSave, onDiscard, saveLabel = 'Save', busyLabel = 'Saving…', discardLabel = 'Discard', testId = 'sw-savebar',
  saveTestId = `${testId}-save`, discardTestId = `${testId}-discard`, focusAfter
}) => {
  const shown = Boolean(on) || Boolean(error)
  const bar = useRef(null)
  const wasShown = useRef(shown)
  useEffect(() => {
    if (wasShown.current && !shown && bar.current) {
      const active = document.activeElement
      if (!active || active === document.body || bar.current.contains(active)) {
        const target = (focusAfter && focusAfter()) || pageHeading(bar.current)
        if (target) {
          if (!FOCUSABLE.test(target.tagName) && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
          target.focus({ preventScroll: true })
        }
      }
    }
    wasShown.current = shown
  }, [shown, focusAfter])
  return (
    <div ref={bar} className={`sw-savebar ${shown ? 'sw-on' : ''}`} data-testid={testId} aria-hidden={!shown} inert={!shown}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mx-auto" style={{ maxWidth: 1180 }}>
        <div className="min-w-0">
          {status || <span className="sw-unsaved-count" role="status">{count > 0 ? unsavedText(count) : on ? 'Unsaved changes' : ''}</span>}
          {error && <span className="text-danger ms-2" role="alert">{error}</span>}
          {error && <ErrorReference error={failure} className="mt-1" />}
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn sw-btn" onClick={onDiscard} disabled={busy} data-testid={discardTestId}>{discardLabel}</button>
          <button type={submit ? 'submit' : 'button'} className="btn sw-btn-primary" disabled={!canSave || busy}
            onClick={submit ? undefined : onSave} data-testid={saveTestId}>{busy ? busyLabel : saveLabel}</button>
        </div>
      </div>
    </div>
  )
}

// The bar inside FormNew (the connection workspace). Save is a real submit
// button, so FormNew's own handleSubmit(onSubmit) runs; Discard resets to
// FormNew's defaultValues and tells the page (onDiscard) so it can drop a
// save error for the discarded edit. Save, like the header's, needs a
// change.
//
// The count is a button listing the changes (unsaved-changes.js): the same
// list the count is, so they can't disagree.
export const SaveBar = ({ error, failure, onDiscard }) => {
  const { reset } = useFormContext()
  const { isDirty, isSubmitting } = useFormState()
  const { changes, undo } = useUnsavedChanges()
  const discard = () => { reset(); onDiscard && onDiscard() }

  return (
    <DockedSaveBar on={changes.length > 0} status={<UnsavedChangesButton changes={changes} onUndo={undo} />}
      error={error} failure={failure} busy={isSubmitting} canSave={isDirty} submit onDiscard={discard} />
  )
}
